/**
 * Governance Entity - RTK Query API
 * API endpoints for Governance domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { fetchAllPages } from '../../../shared/api/paginate';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

// GOV-12: previously invalidated only `{ type: 'GovernanceObjective', id }`
// on update, never the LIST tag, so an open objectives-list subscription
// never refetched after an edit and kept showing stale data.
const updateGovernanceObjectiveInvalidatesTags = (result, error, { id }) => [
  { type: 'GovernanceObjective', id },
  { type: 'GovernanceObjective', id: 'LIST' },
];

// GOV-08: CommitteeListSerializer returns `is_active` (boolean), never a
// `status` field -- the old `item?.status === 'active'` check always
// evaluated false, so `active_committees` was permanently 0.
const transformGovernanceStats = (response) => {
  const records = response?.results || response || [];

  return {
    total_committees: Array.isArray(records) ? records.length : 0,
    active_committees: Array.isArray(records)
      ? records.filter((item) => item?.is_active === true).length
      : 0,
  };
};

// GOV-07: the action-items list sent `?meeting=<id>`, but
// MeetingActionViewSet.get_queryset (apps/governance/views.py) only reads
// `meeting_id` -- the filter matched nothing and every action in the
// tenant was returned instead of just this meeting's.
// GOV-05: renamed from buildAgendaItemsUrl -- these are MeetingAction rows
// (follow-up items), not the agenda, which is Meeting.agenda (a text field).
const buildMeetingActionsUrl = (meetingId) =>
  `/api/v1/governance/meeting-actions/?meeting_id=${encodeURIComponent(meetingId)}`;

// GOV-05: a meeting's status change shows up in its detail (with nested
// actions), every meetings list, and the upcoming-meetings widget.
const meetingLifecycleInvalidatesTags = (result, error, arg) => {
  const id = typeof arg === 'object' ? arg.id : arg;
  return [
    { type: 'Meeting', id },
    { type: 'Meeting', id: 'LIST' },
    { type: 'Meeting', id: 'UPCOMING' },
  ];
};

// GOV-05: MeetingDetailSerializer nests `actions`, so changing an action
// must refetch that meeting's detail as well as its action list.
// D20: completing from the "My action items" page must also refresh that list.
const meetingActionInvalidatesTags = (result, error, { meetingId }) => [
  { type: 'MeetingAction', id: meetingId },
  { type: 'Meeting', id: meetingId },
  { type: 'MeetingAction', id: 'MINE' },
];

export const governanceApiTestUtils = {
  normalizeListResponse,
  updateGovernanceObjectiveInvalidatesTags,
  transformGovernanceStats,
  buildMeetingActionsUrl,
};

// POL-01: getFrameworks above is capped at whatever `limit`/`page_size` the
// caller passes (policy-library-management/index.jsx used to pass
// limit: 100), silently truncating a tenant's policy register past that
// many rows with no truncation notice -- a data-loss-flavored bug for any
// consumer (bulk operations, KPI counts) that assumes it has every policy.
// Mirrors entities/control/api/controlApi.js's fetchAllControls/
// getAllControls pattern: pin `ordering: 'id'` (the model's default
// ordering is not unique -- see apps/governance/tests/
// test_governance_ordering.py) so repeated OFFSET-paginated requests are
// deterministic, and surface `meta.truncated` if the maxPages cap is hit
// rather than silently dropping rows.
export const fetchAllPolicies = (baseQuery, params = {}) =>
  fetchAllPages(
    baseQuery,
    '/api/v1/governance/policies/',
    { ordering: 'id', ...params },
    { pageSize: 500, maxPages: 20 }
  );

export const governanceApi = createApi({
  reducerPath: 'governanceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Committee',
    'Meeting',
    'MeetingAction',
    'PolicyAcknowledgment',
    'GovernanceFramework',
    'PolicyCategory',
    'GovernanceReport',
    'GovernanceObjective',
    'RACIMatrix',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // COMMITTEES
    // =========================================================================

    /**
     * Get paginated list of committees
     */
    getCommittees: builder.query({
      query: ({ page = 1, limit = 20, search = '', status = '' } = {}) => ({
        url: '/api/v1/governance/committees/',
        params: {
          page,
          page_size: limit,
          search: search || undefined,
          status: status || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Committee', id })),
              { type: 'Committee', id: 'LIST' },
            ]
          : [{ type: 'Committee', id: 'LIST' }],
    }),

    /**
     * Get single committee by ID
     */
    getCommitteeById: builder.query({
      query: (id) => `/api/v1/governance/committees/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Committee', id }],
    }),

    /**
     * Create new committee
     */
    createCommittee: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/committees/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Committee', id: 'LIST' }],
    }),

    /**
     * Update committee
     */
    updateCommittee: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/governance/committees/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Committee', id },
        { type: 'Committee', id: 'LIST' },
      ],
    }),

    /**
     * Delete committee
     */
    deleteCommittee: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/governance/committees/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'Committee', id: 'LIST' }],
    }),

    // =========================================================================
    // MEETINGS
    // =========================================================================

    /**
     * Get paginated list of meetings
     */
    getMeetings: builder.query({
      query: ({
        page = 1,
        limit = 20,
        committee = '',
        status = '',
        dateFrom = '',
        dateTo = '',
      } = {}) => ({
        url: '/api/v1/governance/meetings/',
        params: {
          page,
          page_size: limit,
          committee: committee || undefined,
          status: status || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Meeting', id })),
              { type: 'Meeting', id: 'LIST' },
            ]
          : [{ type: 'Meeting', id: 'LIST' }],
    }),

    /**
     * Get single meeting by ID
     */
    getMeetingById: builder.query({
      query: (id) => `/api/v1/governance/meetings/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Meeting', id }],
    }),

    /**
     * Create new meeting
     */
    createMeeting: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/meetings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    /**
     * Update meeting
     */
    updateMeeting: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/governance/meetings/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Meeting', id },
        { type: 'Meeting', id: 'LIST' },
      ],
    }),

    /**
     * Delete meeting
     */
    deleteMeeting: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/governance/meetings/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    // GOV-05: meeting lifecycle. status and minutes are read-only on the
    // serializer; these actions are the only way to change them, and each
    // enforces the scheduled -> in_progress -> completed state machine.
    startMeeting: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/meetings/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: meetingLifecycleInvalidatesTags,
    }),

    /** Complete an in-progress meeting, recording its minutes. */
    completeMeeting: builder.mutation({
      query: ({ id, minutes }) => ({
        url: `/api/v1/governance/meetings/${id}/complete/`,
        method: 'POST',
        body: { minutes },
      }),
      invalidatesTags: meetingLifecycleInvalidatesTags,
    }),

    cancelMeeting: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/meetings/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: meetingLifecycleInvalidatesTags,
    }),

    // =========================================================================
    // MEETING ACTION ITEMS
    // GOV-05: previously named getAgendaItems/addAgendaItem, but they read
    // and write MeetingAction rows (follow-up tasks), not the agenda.
    // =========================================================================

    /**
     * Get action items for a meeting
     */
    getMeetingActions: builder.query({
      query: (meetingId) => buildMeetingActionsUrl(meetingId),
      transformResponse: (response) => response?.results || response || [],
      providesTags: (result, error, meetingId) => [{ type: 'MeetingAction', id: meetingId }],
    }),

    /**
     * Add an action item to a meeting
     */
    createMeetingAction: builder.mutation({
      query: ({ meetingId, ...data }) => ({
        url: `/api/v1/governance/meeting-actions/`,
        method: 'POST',
        body: { meeting: meetingId, ...data },
      }),
      invalidatesTags: meetingActionInvalidatesTags,
    }),

    /**
     * D20: action items assigned to the caller. Self-service on the backend,
     * so it works without governance:read.
     */
    getMyMeetingActions: builder.query({
      query: ({ page = 1 } = {}) => `/api/v1/governance/meeting-actions/mine/?page=${page}`,
      transformResponse: (response) =>
        Array.isArray(response)
          ? { results: response, count: response.length, next: null, previous: null }
          : response || { results: [], count: 0, next: null, previous: null },
      providesTags: [{ type: 'MeetingAction', id: 'MINE' }],
    }),

    completeMeetingAction: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/governance/meeting-actions/${id}/complete/`,
        method: 'POST',
      }),
      invalidatesTags: meetingActionInvalidatesTags,
    }),

    cancelMeetingAction: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/governance/meeting-actions/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: meetingActionInvalidatesTags,
    }),

    // =========================================================================
    // POLICY CATEGORIES
    // =========================================================================

    getPolicyCategories: builder.query({
      query: () => '/api/v1/governance/categories/',
      transformResponse: (response) => response?.results || response || [],
      providesTags: [{ type: 'PolicyCategory', id: 'LIST' }],
    }),

    createPolicyCategory: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/categories/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'PolicyCategory', id: 'LIST' }],
    }),

    // =========================================================================
    // GOVERNANCE FRAMEWORKS (Policy Register)
    // =========================================================================

    /**
     * Get governance frameworks
     */
    getFrameworks: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/governance/policies/',
        params: { page, page_size: limit },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'GovernanceFramework', id })),
              { type: 'GovernanceFramework', id: 'LIST' },
            ]
          : [{ type: 'GovernanceFramework', id: 'LIST' }],
    }),

    /**
     * POL-01: fetch every policy in the tenant's register, paging past
     * getFrameworks' single-page limit instead of silently truncating.
     * Alongside getFrameworks, not replacing it.
     */
    getAllPolicies: builder.query({
      async queryFn(arg = {}, _queryApi, _extraOptions, baseQuery) {
        const { params = {} } = arg;
        const result = await fetchAllPolicies(baseQuery, params);
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: {
            data: result.data,
            total: result.data.length,
            meta: result.meta,
          },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'GovernanceFramework', id })),
              { type: 'GovernanceFramework', id: 'LIST' },
            ]
          : [{ type: 'GovernanceFramework', id: 'LIST' }],
    }),

    /**
     * Get framework by ID
     */
    getFrameworkById: builder.query({
      // encodeURIComponent: ids can come from a deep link (?policy=), ruling R27.
      query: (id) => `/api/v1/governance/policies/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'GovernanceFramework', id }],
    }),

    /**
     * Create framework
     */
    createFramework: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/policies/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'GovernanceFramework', id: 'LIST' }],
    }),

    /**
     * Update framework (policy)
     */
    updateFramework: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/governance/policies/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'GovernanceFramework', id },
        { type: 'GovernanceFramework', id: 'LIST' },
      ],
    }),

    /**
     * Delete a policy. POL-06: backs BulkOperationsBar's bulk-delete
     * action, which previously only console.warn'd and never called any
     * mutation.
     */
    deleteFramework: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/governance/policies/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'GovernanceFramework', id: 'LIST' }],
    }),

    // POL-05/NEW-POL-06: PolicyViewSet's four lifecycle actions. These are
    // the ONLY server-sanctioned way to move a policy's status -- raw
    // PATCH on status/approver is rejected/ignored (see updateFramework
    // above and PolicySerializer). Each invalidates the same tags as
    // updateFramework so the edit modal and list both refetch fresh state.
    // Approval routing C1: takes an id, or { id, approvers } where approvers is
    // { "<flow step order>": [userId] } (ruling R5). The same action resubmits a
    // policy whose latest round was returned for correction.
    submitPolicyForReview: builder.mutation({
      query: (arg) => {
        const { id, approvers } = typeof arg === 'object' ? arg : { id: arg };
        return {
          url: `/api/v1/governance/policies/${encodeURIComponent(id)}/submit_for_review/`,
          method: 'POST',
          body: approvers ? { approvers } : {},
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'GovernanceFramework', id: typeof arg === 'object' ? arg.id : arg },
        { type: 'GovernanceFramework', id: 'LIST' },
      ],
    }),

    approvePolicy: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/policies/${encodeURIComponent(id)}/approve/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GovernanceFramework', id },
        { type: 'GovernanceFramework', id: 'LIST' },
      ],
    }),

    publishPolicy: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/policies/${id}/publish/`,
        method: 'POST',
      }),
      // GOV-09: a newly published policy may be one the user now owes an
      // acknowledgment for.
      invalidatesTags: (result, error, id) => [
        { type: 'GovernanceFramework', id },
        { type: 'GovernanceFramework', id: 'LIST' },
        { type: 'PolicyAcknowledgment', id: 'PENDING' },
      ],
    }),

    archivePolicy: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/policies/${id}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GovernanceFramework', id },
        { type: 'GovernanceFramework', id: 'LIST' },
      ],
    }),

    // NEW-POL-07: draft a new version of a published policy. The server
    // copies the content, keeps the reference code, and archives the old
    // version once the new one is published.
    createPolicyVersion: builder.mutation({
      query: ({ id, version }) => ({
        url: `/api/v1/governance/policies/${id}/create_new_version/`,
        method: 'POST',
        body: { version },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'GovernanceFramework', id },
        { type: 'GovernanceFramework', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // POLICY ACKNOWLEDGMENTS (GOV-09/POL-07, D8 self-service)
    // =========================================================================

    /** Acknowledge a published policy as the signed-in user (no body). */
    acknowledgePolicy: builder.mutation({
      query: (id) => ({
        url: `/api/v1/governance/policies/${id}/acknowledge/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PolicyAcknowledgment', id: 'PENDING' },
        { type: 'PolicyAcknowledgment', id: 'LIST' },
        { type: 'GovernanceFramework', id },
      ],
    }),

    /**
     * Acknowledgments. The server returns only the caller's own rows unless
     * they hold governance:write, who see everyone's.
     */
    getPolicyAcknowledgments: builder.query({
      // Pages through the whole register (cf. POL-01) and flags
      // meta.truncated rather than silently dropping rows.
      async queryFn({ policy } = {}, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllPages(
          baseQuery,
          '/api/v1/governance/acknowledgments/',
          policy ? { policy } : {},
          { pageSize: 500, maxPages: 20 }
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: { data: result.data, total: result.data.length, meta: result.meta } };
      },
      providesTags: [{ type: 'PolicyAcknowledgment', id: 'LIST' }],
    }),

    /** Published policies the signed-in user still has to acknowledge. */
    getMyPendingAcknowledgments: builder.query({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const result = await fetchAllPages(
          baseQuery,
          '/api/v1/governance/policies/pending_acknowledgments/',
          {},
          { pageSize: 100, maxPages: 10 }
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      providesTags: [{ type: 'PolicyAcknowledgment', id: 'PENDING' }],
    }),

    // GOV-06: getBoardMembers/getBoardMemberById/getResolutions/createResolution
    // removed -- they pointed at /delegations/ and /acknowledgments/ (the
    // latter a ReadOnlyModelViewSet, so createResolution's POST always
    // 405'd), neither of which models board members or resolutions. No such
    // model or route exists in this backend, and no caller (page, component,
    // or test) used any of these four hooks. Removed as dead/broken
    // API-layer stubs rather than inventing new backend routes to match a
    // fictional contract, matching established precedent (M-CMP CMP-17,
    // POL-08: pure UI/API stubs with no real backend behind them get removed).

    // =========================================================================
    // REPORTS & STATISTICS
    // =========================================================================

    /**
     * Get governance dashboard statistics
     */
    getGovernanceStats: builder.query({
      query: () => '/api/v1/governance/committees/',
      transformResponse: transformGovernanceStats,
      providesTags: [{ type: 'GovernanceReport', id: 'STATS' }],
    }),

    /**
     * Get upcoming meetings
     */
    getUpcomingMeetings: builder.query({
      query: ({ days = 30 } = {}) => ({
        url: '/api/v1/governance/meetings/',
        params: { days },
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: [{ type: 'Meeting', id: 'UPCOMING' }],
    }),

    // =========================================================================
    // GOVERNANCE OBJECTIVES
    // =========================================================================
    getGovernanceObjectives: builder.query({
      query: ({ page = 1, limit = 20, status = '' } = {}) => ({
        url: '/api/v1/governance/objectives/',
        params: { page, page_size: limit, status: status || undefined },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'GovernanceObjective', id: 'LIST' }],
    }),

    createGovernanceObjective: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/objectives/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'GovernanceObjective', id: 'LIST' }],
    }),

    updateGovernanceObjective: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/governance/objectives/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: updateGovernanceObjectiveInvalidatesTags,
    }),

    // =========================================================================
    // RACI MATRICES
    // =========================================================================
    getRACIMatrices: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/governance/raci-matrices/',
        params: { page, page_size: limit },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'RACIMatrix', id: 'LIST' }],
    }),

    getRACIMatrixById: builder.query({
      query: (id) => `/api/v1/governance/raci-matrices/${id}/`,
      providesTags: (result, error, id) => [{ type: 'RACIMatrix', id }],
    }),

    createRACIMatrix: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/raci-matrices/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'RACIMatrix', id: 'LIST' }],
    }),

    getRACIEntries: builder.query({
      query: ({ matrixId } = {}) => ({
        url: '/api/v1/governance/raci-entries/',
        params: { matrix: matrixId },
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: [{ type: 'RACIMatrix', id: 'ENTRIES' }],
    }),

    createRACIEntry: builder.mutation({
      query: (data) => ({
        url: '/api/v1/governance/raci-entries/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'RACIMatrix', id: 'ENTRIES' }],
    }),
  }),
});

// Export hooks for components
export const {
  // Policy Categories
  useGetPolicyCategoriesQuery,
  useCreatePolicyCategoryMutation,
  // Committees
  useGetCommitteesQuery,
  useGetCommitteeByIdQuery,
  useCreateCommitteeMutation,
  useUpdateCommitteeMutation,
  useDeleteCommitteeMutation,
  // Meetings
  useGetMeetingsQuery,
  useGetMeetingByIdQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
  // Meeting lifecycle (GOV-05)
  useStartMeetingMutation,
  useCompleteMeetingMutation,
  useCancelMeetingMutation,
  // Meeting action items (GOV-05)
  useGetMeetingActionsQuery,
  useGetMyMeetingActionsQuery,
  useCreateMeetingActionMutation,
  useCompleteMeetingActionMutation,
  useCancelMeetingActionMutation,
  // Frameworks
  useGetFrameworksQuery,
  useGetAllPoliciesQuery,
  useGetFrameworkByIdQuery,
  useCreateFrameworkMutation,
  useUpdateFrameworkMutation,
  useDeleteFrameworkMutation,
  // Policy lifecycle (POL-05/NEW-POL-06)
  useSubmitPolicyForReviewMutation,
  useApprovePolicyMutation,
  usePublishPolicyMutation,
  useArchivePolicyMutation,
  useCreatePolicyVersionMutation,
  // Policy acknowledgments (GOV-09)
  useAcknowledgePolicyMutation,
  useGetPolicyAcknowledgmentsQuery,
  useGetMyPendingAcknowledgmentsQuery,
  // Stats
  useGetGovernanceStatsQuery,
  useGetUpcomingMeetingsQuery,
  // Objectives
  useGetGovernanceObjectivesQuery,
  useCreateGovernanceObjectiveMutation,
  useUpdateGovernanceObjectiveMutation,
  // RACI
  useGetRACIMatricesQuery,
  useGetRACIMatrixByIdQuery,
  useCreateRACIMatrixMutation,
  useGetRACIEntriesQuery,
  useCreateRACIEntryMutation,
} = governanceApi;
